import { cartesianAxisLabelTexts, fitLinePlotPadding } from './line-presentation'

const list = value => Array.isArray(value) ? value : value ? [value] : []
const own = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key)
const finite = (value, fallback) => ['number', 'string'].includes(typeof value) && String(value).trim() && Number.isFinite(Number(value)) ? Number(value) : fallback
const textOf = value => value && typeof value === 'object' ? value.value === undefined ? value.name || '' : value.value : value == null ? '' : String(value)
function measure (value, style = {}, engine) {
  const size = Math.max(1, finite(style.fontSize, 12)), text = String(textOf(value))
  const font = [style.fontStyle || 'normal', style.fontWeight || 'normal', size + 'px', style.fontFamily || 'sans-serif'].join(' ')
  return engine && engine.format && engine.format.getTextRect ? engine.format.getTextRect(text, font, undefined, undefined, style.padding, style.rich)
    : { width: text.length * size * .75, height: size * 1.4 }
}

const pixels = (value, size) => typeof value === 'string' && value.trim().endsWith('%') ? finite(parseFloat(value), 0) * finite(size, 0) / 100 : finite(value, 0)

function protectAxisEdges (option, engine, size) {
  const fitted = fitLinePlotPadding(option, size, engine)
  const grids = list(fitted.grid).map(grid => ({ ...grid }))
  let changed = false
  // containLabel 已包含刻度空间，端点保护只补不足部分，避免重复累加。
  const measured = grids.some(grid => grid.containLabel === true) ? list(fitLinePlotPadding({ ...fitted, grid: grids.map(grid => ({ ...grid, left: 0, right: 0, top: 0, bottom: 0, containLabel: false })) }, size, engine).grid) : []
  const reserve = (index, side, required) => {
    const grid = grids[index], dimension = side === 'left' || side === 'right' ? size.width : size.height
    const contained = grid.containLabel !== true ? 0 : Math.max(0, pixels(measured[index] && measured[index][side], dimension) - 2)
    const needed = Math.max(0, Math.ceil(required + 3 - contained - 1e-6))
    if (pixels(grid[side], dimension) < needed) { grid[side] = needed; changed = true }
  }
  ;['x', 'y'].forEach(dimension => list(fitted[dimension + 'Axis']).forEach((axis, index) => {
    const gridIndex = finite(axis.gridIndex, 0), label = { ...(axis.axisLabel && axis.axisLabel.textStyle), ...axis.axisLabel }
    if (!grids[gridIndex] || axis.show === false || label.show === false || label.inside || (axis.type === 'category' && axis.boundaryGap !== false)) return
    const texts = cartesianAxisLabelTexts(fitted, axis, dimension, index, engine)
    if (!texts.length) return
    const extent = value => {
      const box = measure(value, label, engine), angle = finite(label.rotate, 0) * Math.PI / 180
      return dimension === 'x' ? (Math.abs(Math.cos(angle)) * box.width + Math.abs(Math.sin(angle)) * box.height) / 2 : (Math.abs(Math.sin(angle)) * box.width + Math.abs(Math.cos(angle)) * box.height) / 2
    }
    const sides = dimension === 'x' ? ['left', 'right'] : ['bottom', 'top']
    if (axis.inverse) sides.reverse()
    reserve(gridIndex, sides[0], extent(texts[0]))
    reserve(gridIndex, sides[1], extent(texts[texts.length - 1]))
  }))
  return changed ? { ...fitted, grid: Array.isArray(fitted.grid) ? grids : grids[0] } : fitted
}

function lineAxisSpacing (option, settings, custom) {
  if (typeof settings.lineBoundaryGap !== 'boolean' || !(option.series || []).length || option.series.some(series => series.type !== 'line')) return option
  const next = { ...option }
  for (const key of ['xAxis', 'yAxis']) {
    const values = list(option[key]).map((axis, index) => axis.type === 'category' && !own(list(custom[key])[index] || {}, 'boundaryGap') ? { ...axis, boundaryGap: settings.lineBoundaryGap } : axis)
    if (values.length) next[key] = Array.isArray(option[key]) ? values : values[0]
  }
  return next
}

// 自动模式按可见刻度测量所需空间，端点保护与刻度边距取较大值。
export function composeCartesianLayout (option, settings = {}, engine, custom = {}, size = {}) {
  option = lineAxisSpacing(option, settings, custom)
  const grids = list(option.grid)
  if (!grids.length) return option
  if (custom.grid !== undefined) return protectAxisEdges(option, engine, size)
  if (settings.chartPaddingMode === 'manual') {
    const fields = { left: 'gridX', top: 'gridY', right: 'gridX2', bottom: 'gridY2' }
    const manual = grids.map(grid => {
      const next = { ...grid }
      Object.entries(fields).forEach(([side, key]) => { if (own(settings, key) && Number.isFinite(finite(settings[key], NaN))) next[side] = Math.max(0, finite(settings[key], 0)) })
      return next
    })
    return protectAxisEdges({ ...option, grid: Array.isArray(option.grid) ? manual : manual[0] }, engine, size)
  }
  const prepared = { ...option }
  const pureLine = (option.series || []).length && option.series.every(series => series.type === 'line')
  for (const key of ['xAxis', 'yAxis']) {
    const values = list(option[key]).map((axis, index) => {
      let next = axis
      // 当前 ECharts 的 containLabel 仍会测量隐藏轴的刻度，需同步关闭本轮刻度显示。
      if (axis.show === false) next = { ...next, axisLabel: { ...next.axisLabel, show: false } }
      if (pureLine && axis.type === 'category' && !own(list(custom[key])[index] || {}, 'boundaryGap')) next = { ...next, boundaryGap: settings.lineBoundaryGap === true }
      return next
    })
    if (values.length) prepared[key] = Array.isArray(option[key]) ? values : values[0]
  }
  const auto = grids.map(grid => ({ ...grid, left: 4, top: 4, right: 4, bottom: 4, width: undefined, height: undefined, containLabel: false }))
  const reserve = (index, side, value) => { if (auto[index]) auto[index][side] = Math.max(auto[index][side], Math.ceil(value)) }
  for (const dimension of ['x', 'y']) list(prepared[dimension + 'Axis']).forEach(axis => {
    if (axis.show === false) return
    const index = axis.gridIndex || 0
    if (!String(axis.name || '').trim()) return
    const location = axis.nameLocation || 'end', rect = measure(axis.name, axis.nameTextStyle, engine), gap = Math.max(0, finite(axis.nameGap, 15))
    const side = ['middle', 'center'].includes(location) ? axis.position || (dimension === 'x' ? 'bottom' : 'left')
      : dimension === 'x' ? (location === 'start' !== !!axis.inverse ? 'left' : 'right') : (location === 'start' !== !!axis.inverse ? 'bottom' : 'top')
    reserve(index, side, (side === 'left' || side === 'right' ? rect.width : rect.height) + gap + 4)
  })
  let titleSpace = 0
  list(option.title).forEach(title => {
    if (title.show === false || !title.text && !title.subtext) return
    const main = title.text ? measure(title.text, { fontSize: 18, ...title.textStyle }, engine).height : 0
    const sub = title.subtext ? measure(title.subtext, { fontSize: 12, ...title.subtextStyle }, engine).height + finite(title.itemGap, 10) : 0
    const bottom = title.bottom !== undefined || title.top === 'bottom'
    const space = main + sub + 14 + Math.max(0, finite(bottom ? title.bottom : title.top, 0))
    reserve(0, bottom ? 'bottom' : 'top', space)
    if (!bottom) titleSpace = Math.max(titleSpace, space)
  })
  list(option.legend).forEach(legend => {
    if (legend.show !== true || !legend.data || !legend.data.length) return
    const style = { fontSize: 12, ...legend.textStyle }, labels = legend.data.map(item => measure(textOf(item), style, engine))
    const vertical = legend.orient === 'vertical', middle = ['middle', 'center'].includes(legend.top || legend.y)
    if (vertical && middle) {
      const side = legend.right !== undefined || legend.x === 'right' || legend.left === 'right' ? 'right' : 'left'
      reserve(0, side, Math.max(...labels.map(rect => rect.width)) + finite(legend.itemWidth, 25) + 24)
    } else {
      const bottom = legend.bottom !== undefined || legend.top === 'bottom' || legend.y === 'bottom'
      const rows = vertical ? labels.length : 1
      reserve(0, bottom ? 'bottom' : 'top', Math.max(...labels.map(rect => rect.height)) * rows + finite(legend.itemGap, 10) * (rows - 1) + 14 + (bottom ? 0 : titleSpace))
    }
  })
  return protectAxisEdges({ ...prepared, grid: Array.isArray(option.grid) ? auto : auto[0] }, engine, size)
}
